import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw, CreditCard, CheckCircle2, AlertTriangle, User, Building2, FileText, RotateCcw, Zap } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  "payment_intent.succeeded": { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Payment succeeded" },
  "payment_intent.payment_failed": { icon: AlertTriangle, cls: "bg-red-50 text-red-700 border-red-200", label: "Payment failed" },
  "customer.updated": { icon: User, cls: "bg-blue-50 text-blue-700 border-blue-200", label: "Customer updated" },
  "account.updated": { icon: Building2, cls: "bg-violet-50 text-violet-700 border-violet-200", label: "Account updated" },
  "invoice.paid": { icon: FileText, cls: "bg-teal-50 text-teal-700 border-teal-200", label: "Invoice paid" },
  "charge.refunded": { icon: RotateCcw, cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Charge refunded" },
  "checkout.session.completed": { icon: CreditCard, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Checkout completed" },
};

const relTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleString();
};

export default function StripeEventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stripe/events");
      setEvents(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto" data-testid="stripe-events-page">
      <div className="flex items-center justify-between gap-4 mb-1">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Zap className="text-[var(--brand)]" size={24} /> Stripe Events
          </h1>
          <p className="text-sm text-gray-500 mt-1">Recent webhook events received &amp; synced to the app database.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-events">
          <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <Card className="mt-5 overflow-hidden">
        <div className="hidden sm:grid grid-cols-[130px_1fr_90px_70px] gap-3 px-4 py-2.5 bg-gray-50 border-b text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          <span>Received</span><span>Event</span><span className="text-center">Synced</span><span className="text-center">Deliveries</span>
        </div>

        {loading && events.length === 0 && (
          <div className="p-10 text-center text-gray-400 text-sm" data-testid="events-loading">Loading events…</div>
        )}
        {!loading && events.length === 0 && (
          <div className="p-10 text-center text-gray-400 text-sm" data-testid="events-empty">
            No Stripe events received yet. Events appear here as soon as Stripe delivers them.
          </div>
        )}

        <div className="divide-y">
          {events.map((e) => {
            const meta = TYPE_META[e.type] || { icon: CreditCard, cls: "bg-gray-50 text-gray-600 border-gray-200", label: e.type };
            const Icon = meta.icon;
            return (
              <div key={e.id} className="grid sm:grid-cols-[130px_1fr_90px_70px] gap-1 sm:gap-3 px-4 py-3 items-center hover:bg-gray-50/60 transition-colors" data-testid={`event-row-${e.id}`}>
                <span className="text-xs text-gray-500 tabular-nums" title={e.received_at}>{relTime(e.received_at)}</span>
                <div className="min-w-0">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${meta.cls}`}>
                    <Icon size={12} /> {meta.label}
                  </span>
                  <div className="text-xs text-gray-500 mt-1 font-mono truncate">{e.summary}</div>
                </div>
                <span className="text-center">
                  {e.handled
                    ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold" data-testid={`synced-${e.id}`}><CheckCircle2 size={13} /> Yes</span>
                    : <span className="text-gray-400 text-xs">—</span>}
                </span>
                <span className="text-center text-xs text-gray-500 tabular-nums">{e.delivery_count || 1}</span>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
